import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import Button from 'shared/components/UC/Button';
import adminService from '../../services/adminService';
import styles from './RemindModal.module.css';

interface RemindAssignment {
  assignmentId: string;
  applicationNumber: string;
  title: string;
  status: 'pending' | 'accepted_not_started' | 'in_progress';
}

interface RemindModalProps {
  isOpen: boolean;
  evaluatorName: string;
  evaluatorId: string;
  assignments: RemindAssignment[];
  onClose: () => void;
  onSent: () => void;
}

const INVITATION_TEMPLATE =
  'Estimado/a {nombre}, le recordamos que tiene una invitación pendiente de respuesta para evaluar la postulación {folio}. Por favor ingrese a la plataforma para aceptar o declinar.';
const EVALUATION_TEMPLATE =
  'Estimado/a {nombre}, le recordamos que tiene una evaluación pendiente de envío. Por favor ingrese a la plataforma para completarla.';

const RemindModal: React.FC<RemindModalProps> = ({
  isOpen,
  evaluatorName,
  evaluatorId,
  assignments,
  onClose,
  onSent,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [templateType, setTemplateType] = useState<'invitation' | 'evaluation'>('evaluation');
  const [message, setMessage] = useState(EVALUATION_TEMPLATE);
  const [lastReminder, setLastReminder] = useState<any>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedIds(new Set(assignments.map((a) => a.assignmentId)));
    const pendingCount = assignments.filter((a) => a.status === 'pending').length;
    const defaultType = pendingCount > assignments.length / 2 ? 'invitation' : 'evaluation';
    setTemplateType(defaultType);
    setMessage(defaultType === 'invitation' ? INVITATION_TEMPLATE : EVALUATION_TEMPLATE);
    const ids = assignments.map((a) => a.assignmentId);
    setLastReminder(adminService.getLastReminderForAssignments(ids) ?? null);
  }, [isOpen, assignments]);

  useEffect(() => {
    setMessage(templateType === 'invitation' ? INVITATION_TEMPLATE : EVALUATION_TEMPLATE);
  }, [templateType]);

  const toggleAssignment = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const daysSinceLastReminder = lastReminder
    ? Math.floor((Date.now() - new Date(lastReminder.sentAt).getTime()) / (24 * 60 * 60 * 1000))
    : null;

  const handleSend = async () => {
    if (selectedIds.size === 0) return;
    setSending(true);
    try {
      await adminService.sendReminder({
        evaluatorId,
        assignmentIds: Array.from(selectedIds),
        templateType,
        message,
      });
      onSent();
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className="uc-h4">Recordar a {evaluatorName}</h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        <div className={styles.body}>
          {daysSinceLastReminder !== null && daysSinceLastReminder < 3 && (
            <div className={styles.warning}>
              Último recordatorio enviado hace {daysSinceLastReminder} día{daysSinceLastReminder !== 1 ? 's' : ''}.
              Se recomienda esperar al menos 3 días entre recordatorios.
            </div>
          )}

          <div className={styles.section}>
            <p className={`uc-label ${styles.sectionLabel}`}>Postulaciones incluidas</p>
            <ul className={styles.assignmentList}>
              {assignments.map((a) => (
                <li key={a.assignmentId} className={styles.assignmentItem}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(a.assignmentId)}
                      onChange={() => toggleAssignment(a.assignmentId)}
                    />
                    <span className={styles.assignmentNumber}>{a.applicationNumber}</span>
                    <span className={styles.assignmentTitle}>{a.title || a.applicationNumber}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.section}>
            <p className={`uc-label ${styles.sectionLabel}`}>Tipo de recordatorio</p>
            <div className={styles.templateRadios}>
              <label>
                <input
                  type="radio"
                  name="templateType"
                  value="evaluation"
                  checked={templateType === 'evaluation'}
                  onChange={() => setTemplateType('evaluation')}
                />
                Recordatorio de evaluación
              </label>
              <label>
                <input
                  type="radio"
                  name="templateType"
                  value="invitation"
                  checked={templateType === 'invitation'}
                  onChange={() => setTemplateType('invitation')}
                />
                Recordatorio de invitación
              </label>
            </div>
          </div>

          <div className={styles.section}>
            <p className={`uc-label ${styles.sectionLabel}`}>Mensaje</p>
            <textarea
              className={styles.messageArea}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
            <p className={styles.note}>
              {'{nombre}'} y {'{folio}'} serán reemplazados automáticamente al enviar.
            </p>
          </div>
        </div>

        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={selectedIds.size === 0 || sending}
          >
            {sending ? 'Enviando...' : 'Enviar recordatorio'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RemindModal;
